import Renderer from "../Renderer";
import { sanitizeHtml } from "./sanitizeHtml";

const OFFSET = 10;
const VIEWPORT_PADDING = 5;
const ARROW_EDGE = 12;
const FADE_OUT_MS = 250;

let idCounter = 0;

/**
 * Small anchored bubble for the legacy viewer (audio hint, navigation help).
 *
 * Shown and hidden from code only. It prefers the requested side of the target
 * and flips to the opposite side when that side has no room, then stays inside
 * the viewport horizontally with the arrow kept on the target.
 */
export default class Tooltip {
    /**
     * @param {HTMLElement} target
     * @param {object} options handlebars options for the #HB-tooltip template
     * @param {object} config
     * @param {"top"|"bottom"} [config.placement]
     * @param {boolean} [config.interactive] keeps the bubble clickable (close button, links)
     * @returns {Tooltip}
     */
    static createTooltip(
        target,
        options = { close: false, title: "", text: "", buttons: [] },
        { placement = "top", interactive = true } = {},
    ) {
        let content;
        try {
            content = this.renderTooltip(options);
        } catch (error) {
            console.error("MAGLR ERROR: tooltip-template not found!, using text only", error);
        }
        return new Tooltip(target, content || options.text || options.title, { placement, interactive });
    }

    static renderTooltip(options = { close: false, title: "", text: "", buttons: [] }) {
        const template = document.getElementById("HB-tooltip").innerHTML;
        return Renderer.renderSource(template, options);
    }

    static createPopup(options) {
        const container = document.createElement("div");
        container.innerHTML = sanitizeHtml(this.renderTooltip(options));

        return container;
    }

    /**
     * @param {HTMLElement} target
     * @param {string} html
     * @param {object} config
     */
    constructor(target, html, { placement = "top", interactive = true } = {}) {
        this.target = target;
        this.placement = placement;
        this.isVisible = false;

        this.element = document.createElement("div");
        this.element.className = "maglr-tooltip";
        this.element.id = `maglr-tooltip-${++idCounter}`;
        this.element.setAttribute("role", "tooltip");
        this.element.classList.toggle("maglr-tooltip--interactive", interactive);
        this.element.innerHTML =
            `<div class="maglr-tooltip__content">${sanitizeHtml(html)}</div>` +
            '<div class="maglr-tooltip__arrow"></div>';
        this.arrow = this.element.querySelector(".maglr-tooltip__arrow");

        this._frame = null;
        this._hideTimer = null;
        this._scheduleUpdate = () => {
            if (this._frame) return;
            this._frame = requestAnimationFrame(() => {
                this._frame = null;
                this.update();
            });
        };
    }

    show() {
        if (this.isVisible) return this;
        this.isVisible = true;
        clearTimeout(this._hideTimer);

        document.body.appendChild(this.element);
        this.target.setAttribute("aria-describedby", this.element.id);
        window.addEventListener("resize", this._scheduleUpdate);
        window.addEventListener("scroll", this._scheduleUpdate, true);
        this.update();
        // Next frame, so the fade-in transition starts from the hidden state.
        requestAnimationFrame(() => this.isVisible && this.element.classList.add("maglr-tooltip--visible"));
        return this;
    }

    /** Fade out and detach; the instance can be shown again. */
    hide() {
        if (!this.isVisible) return this;
        this.isVisible = false;

        this.element.classList.remove("maglr-tooltip--visible");
        this.target.removeAttribute("aria-describedby");
        window.removeEventListener("resize", this._scheduleUpdate);
        window.removeEventListener("scroll", this._scheduleUpdate, true);
        cancelAnimationFrame(this._frame);
        this._frame = null;
        this._hideTimer = setTimeout(() => this.element.remove(), FADE_OUT_MS);
        return this;
    }

    /** Position next to the target; called on show, resize and scroll. */
    update() {
        const targetRect = this.target.getBoundingClientRect();
        const { offsetWidth: width, offsetHeight: height } = this.element;
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight;

        const spaceAbove = targetRect.top - OFFSET;
        const spaceBelow = viewportHeight - targetRect.bottom - OFFSET;
        let placement = this.placement;
        if (placement === "top" && spaceAbove < height && spaceBelow > spaceAbove) placement = "bottom";
        if (placement === "bottom" && spaceBelow < height && spaceAbove > spaceBelow) placement = "top";

        const top = placement === "top" ? targetRect.top - OFFSET - height : targetRect.bottom + OFFSET;
        const targetCenter = targetRect.left + targetRect.width / 2;
        const maxLeft = Math.max(VIEWPORT_PADDING, viewportWidth - width - VIEWPORT_PADDING);
        const left = Math.min(Math.max(targetCenter - width / 2, VIEWPORT_PADDING), maxLeft);

        this.element.dataset.placement = placement;
        this.element.style.top = `${Math.round(top)}px`;
        this.element.style.left = `${Math.round(left)}px`;
        const arrowLeft = Math.min(Math.max(targetCenter - left, ARROW_EDGE), width - ARROW_EDGE);
        this.arrow.style.left = `${Math.round(arrowLeft)}px`;
    }
}
